import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

export const commentDecorationsKey = new PluginKey("commentDecorations")

// Plugin state is { anchors, deco } where `anchors` is the authoritative list
// of comment ranges ({ threadId, from, to }). We map the anchors ourselves on
// every transaction — crucially keeping ranges that collapse to empty (from ===
// to) so deletions can be detected and reported as orphaned. ProseMirror would
// otherwise silently drop a collapsed inline decoration, hiding the deletion.

function normalize(anchor) {
  return {
    threadId: String(anchor.threadId),
    from: anchor.from,
    to: anchor.to,
  }
}

function validRange(a) {
  return Number.isInteger(a.from) && Number.isInteger(a.to) && a.to > a.from
}

function badgeElement(threadIds) {
  const el = document.createElement("span")
  el.className = "comment-badge"
  el.textContent = String(threadIds.length)
  el.setAttribute("data-thread-ids", threadIds.join(","))
  el.setAttribute("contenteditable", "false")
  return el
}

// Each comment is shown as a small numbered circle at the END of its selection.
// Selections that end at the exact same position share one circle whose number
// is how many selections end there. The full range is only painted transiently,
// while a thread is "active" (flashing).
function buildDeco(doc, anchors, activeId) {
  const decos = []

  // Group valid ranges by their end position.
  const byEnd = new Map()
  for (const a of anchors) {
    if (!validRange(a)) continue
    const list = byEnd.get(a.to) || []
    list.push(a.threadId)
    byEnd.set(a.to, list)
  }

  for (const [pos, threadIds] of byEnd) {
    decos.push(
      Decoration.widget(pos, () => badgeElement(threadIds), {
        side: 1,
        key: `badge-${pos}-${threadIds.join(",")}`,
      })
    )
  }

  // Flash: paint the active thread's whole range while it is being highlighted.
  if (activeId) {
    const active = anchors.find((a) => a.threadId === activeId && validRange(a))
    if (active) {
      decos.push(
        Decoration.inline(active.from, active.to, { class: "comment-flash" })
      )
    }
  }

  return DecorationSet.create(doc, decos)
}

function mapAnchors(anchors, mapping) {
  return anchors.map((a) => ({
    threadId: a.threadId,
    // Match the old inclusiveStart:false / inclusiveEnd:false behaviour: the
    // start doesn't absorb text typed just before it, the end grows with text
    // typed inside the range, and deleting the whole range collapses both ends
    // to the same point.
    from: mapping.map(a.from, 1),
    to: mapping.map(a.to, -1),
  }))
}

export const CommentDecorations = Extension.create({
  name: "commentDecorations",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: commentDecorationsKey,

        state: {
          init() {
            return { anchors: [], deco: DecorationSet.empty, activeId: null }
          },
          apply(tr, value) {
            let anchors = mapAnchors(value.anchors, tr.mapping)
            let activeId = value.activeId
            const action = tr.getMeta(commentDecorationsKey)

            if (action) {
              if (action.type === "set") {
                anchors = action.anchors.map(normalize)
              } else if (action.type === "upsert") {
                const a = normalize(action.anchor)
                anchors = anchors.filter((x) => x.threadId !== a.threadId).concat(a)
              } else if (action.type === "remove") {
                anchors = anchors.filter((x) => x.threadId !== String(action.threadId))
              } else if (action.type === "active") {
                activeId = action.threadId == null ? null : String(action.threadId)
              }
            }

            return { anchors, deco: buildDeco(tr.doc, anchors, activeId), activeId }
          },
        },

        props: {
          decorations(state) {
            return commentDecorationsKey.getState(state).deco
          },
        },
      }),
    ]
  },
})

// --- helpers used by the editor controller --------------------------------

export function setCommentAnchors(editor, anchors) {
  editor.view.dispatch(
    editor.state.tr.setMeta(commentDecorationsKey, { type: "set", anchors })
  )
}

export function upsertCommentAnchor(editor, anchor) {
  editor.view.dispatch(
    editor.state.tr.setMeta(commentDecorationsKey, { type: "upsert", anchor })
  )
}

export function removeCommentAnchor(editor, threadId) {
  editor.view.dispatch(
    editor.state.tr.setMeta(commentDecorationsKey, { type: "remove", threadId })
  )
}

// Highlight a thread's whole range (or pass threadId: null to clear). Driven
// through the decoration layer — not the DOM — so ProseMirror's reconciliation
// doesn't strip it, and so the highlight persists across redraws/edits until
// explicitly changed.
export function setActiveAnchor(editor, threadId) {
  editor.view.dispatch(
    editor.state.tr.setMeta(commentDecorationsKey, { type: "active", threadId })
  )
}

// Live positions of every known comment, keyed by threadId. A range whose text
// was fully deleted is reported with orphaned: true (from === to).
export function commentAnchors(editor) {
  const state = commentDecorationsKey.getState(editor.state)
  if (!state) return []
  return state.anchors.map((a) => ({
    threadId: a.threadId,
    from: a.from,
    to: a.to,
    orphaned: a.from >= a.to,
  }))
}
